import styled from '@emotion/styled';
import { useState, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation, Navigate } from 'react-router-dom';
import { CircularProgress, Dialog, DialogContent, DialogActions } from '@mui/material';
import { useConsumerT } from '../translations/landingConsumer';
import Barcode from '../components/Barcode';
import VideoStoryPlayer from '../components/VideoStoryPlayer';
import ConsumerBackButton from '../components/ConsumerBackButton';
import { useDepositContext } from '../pages/Deposit/DepositPageContext';
import AlertSnackbar from '../components/AlertSnackbar';
import { getTutorialVideos } from '../../config/tutorialVideos';
import { useParcelLoader } from '../hooks/useParcelLoader';

export default function DepositScreenContainer() {
  const t = useConsumerT();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { fetchAlert, alertMessage, dispatch: depositPageDispatch } = useDepositContext();
  const [isLastVideo, setIsLastVideo] = useState(false);
  const videoPlayerRef = useRef(null);
  const { parcel } = useParcelLoader();

  const isFromSelection = !!searchParams.get('type');
  const parcelType = isFromSelection ? searchParams.get('type') : (parcel?.type || searchParams.get('type'));
  const parcelVersion = isFromSelection ? searchParams.get('version') : (parcel?.version || searchParams.get('version'));
  const parcelTag = parcel?.id ? `MP${parcel.id.slice(0, 6).toUpperCase()}` : null;
  const carrierBarCode = parcel?.barCode;
  const returnTo = location.state?.returnTo || (isFromSelection ? '/reception' : null);
  const finalReturnTo = location.state?.finalReturnTo;

  function handleAlertClose() {
    depositPageDispatch({ type: 'PAGE_DEPOSIT_STATES_UPDATE', payload: { fetchAlert: false, alertMessage: '' } });
  }

  function handleGoBack() {
    if (finalReturnTo && (returnTo === '/reception' || returnTo === '/retour')) {
      navigate(returnTo, { state: { returnTo: finalReturnTo } });
    } else if (returnTo) {
      navigate(returnTo);
    } else {
      navigate(-1);
    }
  }

  const videos = getTutorialVideos(parcelType, parcelVersion, 'reception');

  function handleIndexChange(idx, isLast) {
    setIsLastVideo(isLast);
  }

  function handleNextVideo() {
    videoPlayerRef.current?.goNext();
  }

  function handleTutorialClick() {
    const params = new URLSearchParams();
    if (parcelType) params.set('type', parcelType);
    if (parcelVersion) params.set('version', parcelVersion);
    const query = params.toString();
    navigate(`/deposit/tutorial${query ? `?${query}` : ''}`, { state: { returnTo: '/deposit' } });
  }


  return (
    <Page>
      <AlertSnackbar open={fetchAlert} message={alertMessage} onClose={handleAlertClose} />
      <TopBar>
        <ConsumerBackButton
          onBack={handleGoBack}
          variant="light"
          label={t('deposit.title')}
          ariaLabel={t('common.back')}
        />
      </TopBar>

      {/* Mode tutoriel : arrivé depuis /reception avec type/version */}
      {isFromSelection && !parcelType ? (
        /* Chargement des paramètres */
        <ContentWrapper $centered>
          <CircularProgress size={32} sx={{ color: '#56A6C3' }} />
        </ContentWrapper>
      ) : isFromSelection && videos.length > 0 ? (
        <>
          <VideoZone>
            <VideoStoryPlayer
              ref={videoPlayerRef}
              videos={videos}
              onIndexChange={handleIndexChange}
            />
          </VideoZone>
          {isLastVideo ? (
            <CtaButton onClick={() => navigate(returnTo)}>{t('common.back')}</CtaButton>
          ) : (
            <CtaButton onClick={handleNextVideo}>{t('tuto.cta-next')}</CtaButton>
          )}
        </>
      ) : isFromSelection && videos.length === 0 ? (
        <Navigate to="/reception" replace />
      ) : (
        /* Mode normal : colis scanné — toujours les étapes, jamais de vidéo inline */
        <>
          <ScrollArea>
            <ParcelBlock>
              <PackageInfo>
                <IdRow>
                  <IdLabel>{t('deposit.package-identified-label')}</IdLabel>
                  <IdValue>{t('deposit.package-identified-value')}</IdValue>
                </IdRow>
                <IdCode $error={!parcelTag}>{parcelTag || t('deposit.package-not-found')}</IdCode>
              </PackageInfo>
              <MapLink type="button" onClick={() => navigate('/map')}>
                📍 {t('deposit.find-map-link')}
              </MapLink>
            </ParcelBlock>

            <StepCard $expanded>
              <StepHeader>
                <StepNumber>1</StepNumber>
                <StepText>
                  <StepTitle>{t('deposit.step-1-title')}</StepTitle>
                  <StepBody>{t('deposit.step-1-body')}</StepBody>
                </StepText>
              </StepHeader>
              <BarcodeSection>
                {carrierBarCode ? (
                  <BarcodeWrapper>
                    <BarcodeLabel>{t('deposit.barcode-label')}</BarcodeLabel>
                    <Barcode
                      value={carrierBarCode}
                      expandable
                      onDone={() => navigate('/welldone', { state: { flow: 'deposit' } })}
                    />
                  </BarcodeWrapper>
                ) : (
                  <>
                    <NoBarcodeMessage>Aucun code-barre trouvé pour cet emballage</NoBarcodeMessage>
                    <RescanButton onClick={() => navigate('/scan?actionType=returnPackage')}>
                      Rescanner l'emballage
                    </RescanButton>
                  </>
                )}
              </BarcodeSection>
            </StepCard>

            <VideoTile onClick={handleTutorialClick}>
              <VideoTileIcon>▶</VideoTileIcon>
              <VideoTileText>{t('deposit.more-info-link')}</VideoTileText>
            </VideoTile>
          </ScrollArea>

          <CtaButton onClick={() => navigate('/welldone', { state: { flow: 'deposit' } })}>
            {t('deposit.done-cta')}
          </CtaButton>
        </>
      )}
      <Dialog open={!parcel?.id} PaperProps={{ sx: { borderRadius: 3, p: 1, m: 2 } }}>
        <DialogContent sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
          <NoParcelDialogIcon>📦</NoParcelDialogIcon>
          <NoParcelDialogText>{t('deposit.no-parcel-message')}</NoParcelDialogText>
        </DialogContent>
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 1, pb: 2, px: 2 }}>
          <RescanDialogButton onClick={() => navigate('/scan?actionType=returnPackage')}>
            {t('deposit.no-parcel-cta')}
          </RescanDialogButton>
          <HomeDialogButton onClick={() => navigate('/')}>
            {t('bravo.restart-cta')}
          </HomeDialogButton>
        </DialogActions>
      </Dialog>
    </Page>
  );
}

const Page = styled.div`
  height: 100vh;
  height: 100dvh;
  background: ${p => p.theme.palette.background.default};
  font-family: ${p => p.theme.typography.fontFamily};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 12px max(8px, env(safe-area-inset-bottom));
  box-sizing: border-box;
  gap: 6px;
`;

const TopBar = styled.div`
  flex-shrink: 0;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 0;
  ${p => p.$centered && `
    justify-content: center;
  `}
`;

const ScrollArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 28px;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
  width: 100%;
  box-sizing: border-box;
`;


const IdRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

const IdLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: ${p => p.theme.palette.text.disabled};
`;

const IdValue = styled.div`
  font-size: 11px;
  font-weight: 400;
  color: ${p => p.theme.palette.text.primary};
`;

const IdCode = styled.div`
  font-family: ${p => p.theme.typography.monorama.fontFamily};
  font-size: ${p => p.$error ? '13px' : '16px'};
  font-weight: 600;
  letter-spacing: ${p => p.$error ? '0' : '4px'};
  color: ${p => p.$error ? p.theme.palette.text.secondary : p.theme.palette.text.primary};
  text-align: center;
`;


const BarcodeLabel = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: ${p => p.theme.palette.text.disabled};
  text-align: center;
`;

const NoBarcodeMessage = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.palette.error.main};
  text-align: center;
  margin-top: 2px;
`;

const RescanButton = styled.button`
  background: ${p => p.theme.palette.primary.main};
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: ${p => p.theme.typography.fontFamily};
  cursor: pointer;
  transition: opacity 0.2s;
  margin-top: 4px;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;

const VideoZone = styled.div`
  flex: 1;
  min-height: 0;
  max-height: calc(100vh - 150px);
  display: flex;
`;

const CtaButton = styled.button`
  width: 100%;
  background: ${p => p.$variant === 'back' ? 'transparent' : p.theme.palette.primary.main};
  color: ${p => p.$variant === 'back' ? p.theme.palette.text.secondary : '#FFFFFF'};
  border: ${p => p.$variant === 'back' ? `1px solid ${p.theme.palette.border.main}` : 'none'};
  border-radius: 12px;
  padding: 14px 24px;
  font-size: 15px;
  font-weight: 600;
  font-family: ${p => p.theme.typography.fontFamily};
  cursor: pointer;
  transition: opacity 0.2s;
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.8;
  }
`;


const StepCard = styled.div`
  display: flex;
  flex-direction: ${p => p.$expanded ? 'column' : 'row'};
  align-items: ${p => p.$expanded ? 'stretch' : 'flex-start'};
  gap: ${p => p.$expanded ? '12px' : '14px'};
  background: ${p => p.theme.palette.background.paper};
  border: 1px solid ${p => p.theme.palette.border.main};
  border-radius: 12px;
  padding: 12px 14px;
  box-shadow: ${p => p.theme.shadows[1]};
`;

const BarcodeSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 14px 8px 6px;
`;

const PackageInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const BarcodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
`;

const StepNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${p => p.theme.palette.accent.main};
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;


const StepTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${p => p.theme.palette.text.primary};
  line-height: 1.3;
`;

const StepBody = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${p => p.theme.palette.text.secondary};
  line-height: 1.5;
`;

const ParcelBlock = styled.div`
  background: ${p => p.theme.palette.background.paper};
  border: 1px solid ${p => p.theme.palette.border.main};
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  box-shadow: ${p => p.theme.shadows[1]};
`;

const StepHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
`;

const StepText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MapLink = styled.button`
  width: 100%;
  background: transparent;
  border: 1.5px solid ${p => p.theme.palette.primary.main};
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 600;
  color: ${p => p.theme.palette.primary.main};
  font-family: ${p => p.theme.typography.fontFamily};
  cursor: pointer;
  text-align: center;
  &:active {
    opacity: 0.75;
  }
`;

const VideoTile = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 0;
  background: #FFFFFF;
  border: 1px solid ${p => p.theme.palette.accent.main};
  border-radius: 12px;
  padding: 14px 16px;
  cursor: pointer;
  transition: opacity 0.2s, transform 0.15s;
  flex-shrink: 0;
  font-family: ${p => p.theme.typography.fontFamily};

  &:hover {
    opacity: 0.92;
  }

  &:active {
    transform: scale(0.98);
    opacity: 0.85;
  }
`;

const VideoTileIcon = styled.span`
  font-size: 14px;
  line-height: 1;
  color: ${p => p.theme.palette.accent.main};
`;

const VideoTileText = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${p => p.theme.palette.accent.main};
  letter-spacing: 0.2px;
`;

const NoParcelDialogIcon = styled.div`
  font-size: 40px;
  margin-bottom: 12px;
  line-height: 1;
`;

const NoParcelDialogText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.theme.palette.text.primary};
  line-height: 1.6;
`;

const RescanDialogButton = styled.button`
  width: 100%;
  background: ${p => p.theme.palette.primary.main};
  color: #FFFFFF;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  font-family: ${p => p.theme.typography.fontFamily};
  cursor: pointer;
  transition: opacity 0.2s;

  &:active { opacity: 0.8; }
`;

const HomeDialogButton = styled.button`
  width: 100%;
  margin-left: 0 !important;
  background: transparent;
  color: ${p => p.theme.palette.text.secondary};
  border: 1px solid ${p => p.theme.palette.border.main};
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 13px;
  font-weight: 500;
  font-family: ${p => p.theme.typography.fontFamily};
  cursor: pointer;
  transition: opacity 0.2s;

  &:active { opacity: 0.8; }
`;
